import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Form, Input, Modal } from 'antd';
import { NAMESPACE } from '@/constants';
import type { IoDocument } from '@/api';

interface Props {
  /** The document being cancelled, or null when the dialog is closed. */
  document: IoDocument | null;
  /** True once the document has been signed by all parties. */
  isSigned: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (message: string) => void;
}

const MAX_REASON = 500;

/**
 * Confirms cancelling an IO and collects the reason.
 *
 * The reason is required because it does not stay in NRVE: the provider sends
 * it to everyone who signed, so an empty one leaves them with a cancellation
 * and no explanation.
 */
export const CancelIoModal: FC<Props> = ({
  document,
  isSigned,
  isSubmitting,
  onCancel,
  onConfirm
}) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  /* Each cancellation starts from a clean box — a reason left over from a
     previous document would be worse than none. */
  useEffect(() => {
    if (document) {
      setReason('');
      setTouched(false);
    }
  }, [document]);

  const trimmed = reason.trim();
  const isInvalid = touched && !trimmed;

  const handleConfirm = () => {
    setTouched(true);
    if (trimmed) onConfirm(trimmed);
  };

  return (
    <Modal
      open={Boolean(document)}
      title={t('ioCancelTitle')}
      okText={t('ioCancelConfirmAction')}
      okButtonProps={{ danger: true, disabled: !trimmed, loading: isSubmitting }}
      cancelText={t('ioCancelDismiss')}
      onOk={handleConfirm}
      onCancel={onCancel}
      destroyOnHidden
    >
      {isSigned ? (
        /* A signed IO is an executed agreement, so cancelling it is a bigger
           step than withdrawing one still out for signature. */
        <Alert
          type="warning"
          showIcon
          message={t('ioCancelSignedWarningTitle')}
          description={t('ioCancelSignedWarningBody')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <p>
        {t(isSigned ? 'ioCancelPromptSigned' : 'ioCancelPrompt', {
          name: document?.messageTitle ?? ''
        })}
      </p>

      <Form layout="vertical">
        <Form.Item
          label={t('ioCancelReasonLabel')}
          required
          validateStatus={isInvalid ? 'error' : undefined}
          help={isInvalid ? t('ioCancelReasonRequired') : t('ioCancelReasonHint')}
        >
          <Input.TextArea
            autoFocus
            rows={3}
            maxLength={MAX_REASON}
            /* The counter renders below the field and would sit on top of the
               help text, so the field carries the extra room for it. */
            showCount
            style={{ marginBottom: 18 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={t('ioCancelReasonPlaceholder')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
